
"use client";

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updatePassword,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  type UserCredential,
} from 'firebase/auth';
import { auth, actionCodeSettings } from '@/lib/firebase';
import { apiClient } from '@/lib/api-client';
import type { UserProfile } from '@/lib/types';
import { useMobileSubscription } from '@/hooks/use-mobile-subscription';
import { subscriptionService } from '@/lib/subscription-service';
import { Capacitor } from '@capacitor/core';
import { signInWithGoogle as googleSignIn, signInWithApple as appleSignIn } from '@/lib/social-auth';

import {
  clearUserPreferences,
  getCachedUserProfile,
  setCachedUserProfile,
  getCachedVipStatus,
  setCachedVipStatus
} from '@/lib/storage';
import { setAnalyticsUserId, clearAnalyticsUserId, setAnalyticsUserProperty, trackEvent, AnalyticsEvents } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isSubscribed: boolean;
  signUp: (email: string, password: string) => Promise<UserCredential>;
  logIn: (email: string, password: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithApple: () => Promise<UserCredential>;
  resetPassword: (email: string) => Promise<void>;
  logOut: () => Promise<void>;
  reauthenticate: (password: string) => Promise<any>;
  updateUserPassword: (password: string) => Promise<void>;
  updateUserEmail: (email: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isVip, setIsVip] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get mobile subscription status if on native platform
  // Pass userId so RevenueCat can identify the user's subscription
  const { isSubscribed: isMobileSubscribed, currentSubscription } = useMobileSubscription(user?.uid);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        // Optimistic cache check: unblock UI immediately if cached data exists
        const cachedProfile = getCachedUserProfile(user.uid);
        const cachedVip = getCachedVipStatus(user.uid);

        if (cachedProfile) {
          setUserProfile(cachedProfile);
        }
        if (cachedVip !== null) {
          setIsVip(cachedVip);
        }

        // Only block loading spinner if we have no cached profile
        if (cachedProfile && cachedVip !== null) {
          setLoading(false);
        } else {
          setLoading(true);
        }

        // Set analytics user ID
        setAnalyticsUserId(user.uid);
        try {
          // Parallelize VIP and profile network requests
          const [vipResponse, profileResponse] = await Promise.all([
            apiClient.getVipStatus(),
            apiClient.getUserProfile()
          ]);

          if (vipResponse.success && vipResponse.data) {
            const freshVip = vipResponse.data.isVip || false;
            setIsVip(freshVip);
            setCachedVipStatus(user.uid, freshVip);
          }

          if (profileResponse.success) {
            if (profileResponse.data) {
              setUserProfile(profileResponse.data);
              setCachedUserProfile(user.uid, profileResponse.data);
            } else {
              // Create a profile for a new user if one doesn't exist
              const newUserProfile: Omit<UserProfile, 'id'> = {
                email: user.email!,
                subscription: 'demo', // New users start in demo mode
              };
              const createResponse = await apiClient.createUserProfile(newUserProfile);
              if (createResponse.success && createResponse.data) {
                setUserProfile(createResponse.data);
                setCachedUserProfile(user.uid, createResponse.data);
              }
            }
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          if (!cachedProfile) {
            setIsVip(false);
            setUserProfile(null);
          }
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setIsVip(false);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const logOut = async () => {
    // Track logout event before clearing user
    trackEvent(AnalyticsEvents.LOGOUT);
    // Clear persisted state on logout
    clearUserPreferences();
    // Clear analytics user ID
    clearAnalyticsUserId();

    // Log out of native subscription service (RevenueCat)
    try {
      await subscriptionService.logOut();
    } catch (e) {
      console.warn('Error logging out of subscription service:', e);
    }

    await signOut(auth);
    setUser(null);
    setUserProfile(null);
    setIsVip(false);
    navigate('/login', { replace: true });
  };

  const reauthenticate = (password: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("User not found");
    const credential = EmailAuthProvider.credential(user.email, password);
    return reauthenticateWithCredential(user, credential);
  }

  const updateUserPassword = (password: string) => {
    if (!auth.currentUser) throw new Error("User not found");
    return updatePassword(auth.currentUser, password);
  }

  const updateUserEmail = (email: string) => {
    if (!auth.currentUser) throw new Error("User not found");
    // Use verifyBeforeUpdateEmail to send verification link to new email
    return verifyBeforeUpdateEmail(auth.currentUser, email, actionCodeSettings);
  }

  /**
   * Refresh user profile from backend
   * Call this after subscription changes to update the user's subscription status
   */
  const refreshUserProfile = useCallback(async () => {
    if (!auth.currentUser) {
      return;
    }

    try {
      const currentUid = auth.currentUser.uid;
      // Parallelize VIP and profile refresh
      const [vipResponse, profileResponse] = await Promise.all([
        apiClient.getVipStatus(),
        apiClient.getUserProfile()
      ]);

      if (vipResponse.success && vipResponse.data) {
        const freshVip = vipResponse.data?.isVip || false;
        setIsVip(freshVip);
        setCachedVipStatus(currentUid, freshVip);
      }

      if (profileResponse.success && profileResponse.data) {
        setUserProfile(profileResponse.data);
        setCachedUserProfile(currentUid, profileResponse.data);
      }
    } catch (error) {
      console.error("Error refreshing user profile:", error);
    }
  }, []);

  // Track previous subscription status to detect changes
  const prevSubscriptionStatusRef = useRef<string | undefined>(undefined);

  // Refresh user profile when mobile subscription status changes to active
  useEffect(() => {
    if (Capacitor.isNativePlatform() && currentSubscription?.status === 'active') {
      const prevStatus = prevSubscriptionStatusRef.current;
      const currentStatus = currentSubscription.status;

      // Only refresh if subscription just became active (status changed)
      // or if user profile doesn't show active yet
      if (prevStatus !== currentStatus || userProfile?.subscription !== 'active') {
        refreshUserProfile();
      }

      // Update the ref to track current status
      prevSubscriptionStatusRef.current = currentStatus;
    } else {
      // Update ref even if not active
      prevSubscriptionStatusRef.current = currentSubscription?.status;
    }
  }, [currentSubscription?.status, userProfile?.subscription, Capacitor.isNativePlatform(), refreshUserProfile]);

  // Determine subscription status - VIP users always have access, otherwise check platform-specific subscription
  const isSubscribed = isVip || (Capacitor.isNativePlatform()
    ? isMobileSubscribed
    : userProfile?.subscription === 'active');

  // Track subscription status as a user property whenever it changes
  useEffect(() => {
    if (user) {
      const status = isVip ? 'vip' : isSubscribed ? 'active' : (userProfile?.subscription || 'demo');
      setAnalyticsUserProperty('subscription_status', status);
      setAnalyticsUserProperty('platform', Capacitor.getPlatform());
    }
  }, [user, isVip, isSubscribed, userProfile?.subscription]);

  const value = {
    user,
    userProfile,
    loading,
    isSubscribed,
    signUp: (email: string, password: string) => createUserWithEmailAndPassword(auth, email, password),
    logIn: (email: string, password: string) => signInWithEmailAndPassword(auth, email, password),
    signInWithGoogle: googleSignIn,
    signInWithApple: appleSignIn,
    resetPassword: (email: string) => sendPasswordResetEmail(auth, email, actionCodeSettings),
    logOut,
    reauthenticate,
    updateUserPassword,
    updateUserEmail,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
